// Thin fetch wrapper replacing the source's storage.get/set(DB_KEYS.X, ...)
// localStorage calls with real REST calls through the gateway (same origin,
// no CORS needed - see docs/DECISIONS.md). One function per endpoint.

const TOKEN_KEY = "woof_token";

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}
export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  // "details" carries a list of specific sub-errors when the backend sends
  // one (e.g. the import structure guardrail's per-cell mismatch list) -
  // undefined for every other endpoint, which only ever sends a single
  // "error" message.
  constructor(public status: number, message: string, public details?: string[]) {
    super(message);
  }
}

function errorDetails(payload: unknown): string[] | undefined {
  if (typeof payload === "object" && payload && "details" in payload) {
    const d = (payload as { details: unknown }).details;
    if (Array.isArray(d) && d.every((x) => typeof x === "string")) return d;
  }
  return undefined;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["content-type"] = "application/json";

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();
  const message = typeof payload === "object" && payload && "error" in payload ? String((payload as { error: unknown }).error) : String(payload);

  if (res.status === 401) {
    // /auth/login and /auth/change-password return 401 for their own
    // business-logic reasons (wrong password, wrong old password) - that's
    // not a dead session, so it must not wipe the token / force a reload,
    // and the real backend message should reach the user instead of being
    // overwritten. Every other endpoint's 401 comes from the gateway's own
    // auth guard rejecting a missing/invalid/expired token, which is a real
    // session expiry.
    if (path !== "/auth/login" && path !== "/auth/change-password") {
      clearToken();
      window.location.reload();
      throw new ApiError(401, "Session expirée");
    }
    throw new ApiError(401, message);
  }

  if (!res.ok) {
    throw new ApiError(res.status, message, errorDetails(payload));
  }
  return payload as T;
}

// Separate from request() because it must send a FormData body and must NOT
// set a content-type header itself - the browser generates the multipart
// boundary, and setting content-type manually on a FormData body breaks it.
async function uploadFile<T>(method: string, path: string, file: File): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`/api${path}`, { method, headers, body: formData });

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();
  const message = typeof payload === "object" && payload && "error" in payload ? String((payload as { error: unknown }).error) : String(payload);

  if (res.status === 401) {
    clearToken();
    window.location.reload();
    throw new ApiError(401, "Session expirée");
  }
  if (!res.ok) throw new ApiError(res.status, message, errorDetails(payload));
  return payload as T;
}

// Spreadsheet import preview: like uploadFile, but also carries a siteCode
// form field alongside the file (the importer needs to know which
// investigator site the upload is scoped to before it can even authorize
// the request - see services/*/src/authz.ts's authorizeImport()).
async function uploadImportFile<T>(path: string, siteCode: string, file: File): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["authorization"] = `Bearer ${token}`;
  const formData = new FormData();
  formData.append("siteCode", siteCode);
  formData.append("file", file);

  const res = await fetch(`/api${path}`, { method: "POST", headers, body: formData });

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();
  const message = typeof payload === "object" && payload && "error" in payload ? String((payload as { error: unknown }).error) : String(payload);

  if (res.status === 401) {
    clearToken();
    window.location.reload();
    throw new ApiError(401, "Session expirée");
  }
  if (!res.ok) throw new ApiError(res.status, message, errorDetails(payload));
  return payload as T;
}

function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][];
  if (entries.length === 0) return "";
  return "?" + new URLSearchParams(entries).toString();
}

// ---- auth ----
export const authApi = {
  login: (username: string, password: string) => request<{ token: string; user: any }>("POST", "/auth/login", { username, password }),
  changePassword: (body: { oldPassword?: string; newPassword: string }) => request<{ ok: true }>("POST", "/auth/change-password", body),
  me: () => request<any>("GET", "/auth/me"),
};

// ---- users / sites ----
export const usersApi = {
  list: () => request<any[]>("GET", "/users"),
  // Non-admin-gated minimal directory ({id, username, fullName, role}) for
  // "assign to" pickers - any authenticated user can call this, unlike list().
  directory: () => request<any[]>("GET", "/users/directory"),
  create: (body: { username: string; role: string; sites: string[]; studyIds?: number[]; fullName: string; email?: string; password?: string }) => request<any>("POST", "/users", body),
  update: (id: string, body: Partial<{ role: string; sites: string[]; studyIds: number[]; fullName: string; email: string }>) => request<any>("PATCH", `/users/${id}`, body),
  deactivate: (id: string, reason?: string) => request<{ ok: true }>("POST", `/users/${id}/deactivate`, { reason }),
  resetPassword: (id: string) => request<{ ok: true }>("POST", `/users/${id}/reset-password`),
};
export const sitesApi = {
  list: () => request<any[]>("GET", "/sites"),
  create: (body: { code: string; name: string; country?: string }) => request<any>("POST", "/sites", body),
  setActive: (code: string, active: boolean) => request<any>("PATCH", `/sites/${code}`, { active }),
};

// ---- notification settings ----
export const notificationSettingsApi = {
  get: () => request<{ sponsorEmail: string | null }>("GET", "/settings/notifications"),
  update: (sponsorEmail: string) => request<{ sponsorEmail: string | null }>("PATCH", "/settings/notifications", { sponsorEmail }),
};

// ---- studies ----
export const studiesApi = {
  list: () => request<any[]>("GET", "/studies"),
  get: (id: number | string) => request<any>("GET", `/studies/${id}`),
  create: (body: { name: string; description?: string; siteCodes?: string[]; species?: "animal" | "human" }) => request<any>("POST", "/studies", body),
  update: (id: number | string, body: { name?: string; description?: string; siteCodes?: string[]; species?: "animal" | "human" }) =>
    request<any>("PATCH", `/studies/${id}`, body),
  remove: (id: number | string) => request<void>("DELETE", `/studies/${id}`),
};

// ---- patients ----
export const patientsApi = {
  list: (status?: string, studyId?: number | null) => request<any[]>("GET", `/patients${qs({ status, studyId: studyId != null ? String(studyId) : undefined })}`),
  get: (id: string) => request<any>("GET", `/patients/${id}`),
  create: (body: any) => request<any>("POST", "/patients", body),
  update: (id: string, body: any) => request<any>("PATCH", `/patients/${id}`, body),
  setStatus: (id: string, status: string, reason?: string) => request<any>("POST", `/patients/${id}/status`, { status, reason }),
  statusHistory: (id: string) => request<any[]>("GET", `/patients/${id}/status-history`),
};

// ---- clinical-data: visits/sections ----
export const visitsApi = {
  list: (pid: string) => request<any[]>("GET", `/patients/${pid}/visits`),
  get: (pid: string, vk: string) => request<{ visit: any; sections: Record<string, any>; derived: any }>("GET", `/patients/${pid}/visits/${vk}`),
  setDate: (pid: string, vk: string, visitDate: string | null) => request<any>("PATCH", `/patients/${pid}/visits/${vk}`, { visitDate }),
  putSection: (pid: string, vk: string, sectionId: string, data: Record<string, unknown>) =>
    request<{ visit: any; sections: Record<string, any>; derived: any }>("PUT", `/patients/${pid}/visits/${vk}/sections/${sectionId}`, data),
  createExtra: (pid: string) => request<any>("POST", `/patients/${pid}/visits`),
  irecistSummary: (pid: string) => request<any[]>("GET", `/patients/${pid}/irecist-summary`),
};

export const mhApi = {
  list: (pid: string) => request<any[]>("GET", `/patients/${pid}/mh`),
  create: (pid: string, body: any) => request<any>("POST", `/patients/${pid}/mh`, body),
  remove: (pid: string, id: string, reason?: string) => request<{ ok: true }>("DELETE", `/patients/${pid}/mh/${id}`, { reason }),
};
export const cmApi = {
  list: (pid: string) => request<any[]>("GET", `/patients/${pid}/cm`),
  create: (pid: string, body: any) => request<any>("POST", `/patients/${pid}/cm`, body),
  update: (pid: string, id: string, body: any) => request<any>("PATCH", `/patients/${pid}/cm/${id}`, body),
  remove: (pid: string, id: string, reason?: string) => request<{ ok: true }>("DELETE", `/patients/${pid}/cm/${id}`, { reason }),
};
export const extraAdminsApi = {
  list: (pid: string) => request<any[]>("GET", `/patients/${pid}/extra-admins`),
  create: (pid: string, body: any) => request<any>("POST", `/patients/${pid}/extra-admins`, body),
  update: (pid: string, id: string, body: any) => request<any>("PATCH", `/patients/${pid}/extra-admins/${id}`, body),
  remove: (pid: string, id: string, reason?: string) => request<{ ok: true }>("DELETE", `/patients/${pid}/extra-admins/${id}`, { reason }),
};
export const qolNotesApi = {
  list: (pid: string) => request<any[]>("GET", `/patients/${pid}/qol-notes`),
  create: (pid: string, note: string, visitKey?: string) => request<any>("POST", `/patients/${pid}/qol-notes`, { note, visitKey }),
};
export const referenceApi = {
  get: <T = any>(key: string) => request<T>("GET", `/reference/${key}`),
};

// ---- safety ----
export const aeApi = {
  list: (patientId?: string) => request<any[]>("GET", `/adverse-events${qs({ patientId })}`),
  get: (id: string) => request<any>("GET", `/adverse-events/${id}`),
  create: (body: any) => request<any>("POST", "/adverse-events", body),
  update: (id: string, body: any) => request<any>("PATCH", `/adverse-events/${id}`, body),
};
export const deviationsApi = {
  list: (patientId?: string) => request<any[]>("GET", `/deviations${qs({ patientId })}`),
  get: (id: string) => request<any>("GET", `/deviations/${id}`),
  create: (body: any) => request<any>("POST", "/deviations", body),
  update: (id: string, body: any) => request<any>("PATCH", `/deviations/${id}`, body),
  updateCapa: (id: string, body: { description?: string; action?: string }) => request<any>("PATCH", `/deviations/${id}/capa`, body),
};

// CAPA corrective/preventive actions, nested under a deviation.
export const deviationActionsApi = {
  list: (deviationId: string) => request<any[]>("GET", `/deviations/${deviationId}/actions`),
  create: (deviationId: string, body: { kind: "corrective" | "preventive"; description: string; dueDate?: string }) =>
    request<any>("POST", `/deviations/${deviationId}/actions`, body),
  update: (deviationId: string, actionId: string, body: { description?: string; dueDate?: string | null }) =>
    request<any>("PATCH", `/deviations/${deviationId}/actions/${actionId}`, body),
  remove: (deviationId: string, actionId: string) => request<{ ok: true }>("DELETE", `/deviations/${deviationId}/actions/${actionId}`),
  uploadDeliverable: (deviationId: string, actionId: string, file: File) =>
    uploadFile<any>("PUT", `/deviations/${deviationId}/actions/${actionId}/deliverable`, file),
  removeDeliverable: (deviationId: string, actionId: string) =>
    request<any>("DELETE", `/deviations/${deviationId}/actions/${actionId}/deliverable`),
  deliverableUrl: (deviationId: string, actionId: string) => `/api/deviations/${deviationId}/actions/${actionId}/deliverable`,
};

// ---- queries ----
export const queriesApi = {
  list: (params: { patientId?: string; status?: string } = {}) => request<any[]>("GET", `/queries${qs(params)}`),
  get: (id: string) => request<any>("GET", `/queries/${id}`),
  history: (id: string) => request<any[]>("GET", `/queries/${id}/history`),
  create: (body: any) => request<any>("POST", "/queries", body),
  answer: (id: string, answer: string, comment?: string) => request<any>("POST", `/queries/${id}/answer`, { answer, comment }),
  close: (id: string, comment?: string) => request<any>("POST", `/queries/${id}/close`, { comment }),
  reopen: (id: string, comment?: string) => request<any>("POST", `/queries/${id}/reopen`, { comment }),
  reassign: (id: string, assignedTo: string, comment?: string) => request<any>("PATCH", `/queries/${id}`, { assignedTo, comment }),
};

// ---- audit ----
export const auditApi = {
  list: (params: { user?: string; scope?: string; from?: string; to?: string; q?: string; page?: number; pageSize?: number } = {}) =>
    request<{ entries: any[]; total: number }>(
      "GET",
      `/audit${qs({ user: params.user, scope: params.scope, from: params.from, to: params.to, q: params.q, page: params.page?.toString(), pageSize: params.pageSize?.toString() })}`
    ),
  exportCsvUrl: (params: Record<string, string | undefined>) => `/api/audit/export.csv${qs(params)}`,
};

export const exportAllPatientsCsvUrl = "/api/export/all-patients.csv";

// ---- admin: spreadsheet import ----
// Each importer's preview() returns { summary, patientsExist, siteMismatches,
// operations } - "operations" is the fully-resolved write plan, echoed back
// verbatim to commit() (no server-side session/cache, no file re-upload).
// Only résultats/qol previews populate "changes" - CM/AE are insert-only
// (no upsert key), so there's no partial "this field differs" concept for
// them, just new-vs-already-imported at the whole-row level.
export interface ImportFieldChange {
  patientId: string;
  visitKey: string;
  section: string;
  field: string;
  dbValue: unknown;
  fileValue: unknown;
}
export interface ImportPreview {
  summary: Record<string, unknown>;
  patientsExist: Record<string, boolean>;
  siteMismatches: string[];
  changes?: ImportFieldChange[];
  operations: { siteCode: string; patients: any[] };
}
export interface ImportCommitResult {
  patientsProcessed: number;
  errors: string[];
  [key: string]: unknown;
}
function importer(kind: string) {
  return {
    preview: (siteCode: string, file: File) => uploadImportFile<ImportPreview>(`/import/${kind}/preview`, siteCode, file),
    commit: (operations: { siteCode: string; patients: any[] }) => request<ImportCommitResult>("POST", `/import/${kind}/commit`, operations),
  };
}
export const importApi = {
  resultats: importer("resultats"),
  qol: importer("qol"),
  cm: importer("cm"),
  ae: importer("ae"),
};
